<template>
  <div class="credit-pricing-calculator" data-testid="credit-pricing-calculator">
    <h3 class="calculator-title">{{ t('Calculadora de precificação') }}</h3>

    <!-- Basis banner -->
    <div
      class="basis-banner"
      :class="usageBasis.basis === 'fallback' ? 'basis-banner--fallback' : 'basis-banner--real'"
      data-testid="credit-pricing-basis"
    >
      <i :class="usageBasis.basis === 'fallback' ? 'pi pi-exclamation-triangle' : 'pi pi-chart-line'" />
      <span>{{
        usageBasis.basis === 'fallback'
          ? t('Estimativa — sem dados suficientes; usando ~1 crédito por pergunta')
          : t('Baseado no consumo real do período')
      }}</span>
    </div>

    <!-- Dados reais -->
    <div class="tiles-row">
      <stats-card
        data-testid="credit-pricing-avg-credits"
        :value="usageBasis.avgCreditsPerPrompt.toLocaleString('pt-BR', { maximumFractionDigits: 3 }) + ' ' + t('cr')"
        :label="t('Média cr/pergunta')"
        icon="pi pi-bolt"
      />
      <stats-card
        data-testid="credit-pricing-cogs"
        :value="fmtUsd(usageBasis.cogsUsdPerPrompt, 6)"
        :label="t('Custo/pergunta no peg (USD)')"
        icon="pi pi-credit-card"
        variant="danger"
      />
    </div>

    <!-- Knobs -->
    <div class="knobs-row">
      <input-number
        elem-id="credit_pricing_retail_price"
        data-testid="credit-pricing-price-input"
        :label="t('Preço de venda do pacote (USD)')"
        :prefix="'$ '"
        mode="decimal"
        :minFractionDigits="2"
        :maxFractionDigits="2"
        :min="0"
        :step="1"
        v-model="retailPriceUsd"
      />
      <input-number
        elem-id="credit_pricing_pack_size"
        data-testid="credit-pricing-pack-size-input"
        :label="t('Tamanho do pacote (créditos)')"
        mode="decimal"
        :minFractionDigits="0"
        :maxFractionDigits="0"
        :min="1"
        :step="100"
        v-model="packSizeCredits"
      />
    </div>

    <!-- Resultado — identidade §2.4 -->
    <div class="tiles-row">
      <stats-card
        data-testid="credit-pricing-per-credit"
        :value="fmtUsd(identity.retailPricePerCreditUsd, 4)"
        :label="t('Preço por crédito (USD)')"
        icon="pi pi-dollar"
      />
      <stats-card
        data-testid="credit-pricing-margin-multiple"
        :value="formatMarginMultiple(identity.marginMultiple)"
        :label="t('Múltiplo de margem (preço ÷ peg)')"
        icon="pi pi-chart-bar"
        variant="info"
      />
      <stats-card
        data-testid="credit-pricing-margin"
        :value="fmtUsd(identity.grossMarginPerCreditUsd, 4) + ' (' + identity.marginPct.toFixed(1) + '%)'"
        :label="t('Margem bruta por crédito (USD)')"
        icon="pi pi-percentage"
        variant="success"
      />
    </div>

    <!-- Aplicar a um pacote real -->
    <div class="apply-row">
      <Button
        :label="t('Aplicar a um pacote')"
        data-testid="credit-pricing-apply-to-pack"
        @click="handleApplyToPack"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import type { AiCredits_AdminOverviewResponseDados } from '../../../contracts/AiCredits_AdminOverviewResponse';
import type { AiCredits_PackRow } from '../../../contracts/AiCredits_PackRow';
import StatsCard from '../../../ui/StatsCard.vue';
import { MICRO_PER_CREDIT } from '../../../composables/useCreditWallet';
import { computeMarginIdentity, computeUsageBasis, formatMarginMultiple } from './creditPricing';

const { t } = useI18n();

interface Props {
  dados: AiCredits_AdminOverviewResponseDados | null;
}

const props = defineProps<Props>();

const retailPriceUsd = ref(100);
const packSizeCredits = ref(1000);

/** peg_usd is the sole global cost anchor — always read from dados, never hardcoded. */
const pegUsd = computed(() => (props.dados ? parseFloat(props.dados.peg_usd) || 0 : 0));

const usageBasis = computed(() =>
  computeUsageBasis(
    props.dados
      ? { totalMicro: props.dados.total_micro, settleCount: props.dados.settle_count }
      : null,
    pegUsd.value,
  ),
);

const identity = computed(() =>
  computeMarginIdentity({
    retailPriceUsd: retailPriceUsd.value,
    packSizeCredits: packSizeCredits.value,
    pegUsd: pegUsd.value,
  }),
);

function fmtUsd(v: number, dp = 4): string {
  return '$ ' + v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/**
 * Hands the simulated pack size off to a real pack instead of throwing it away. Reuses the
 * existing pack modal/form (no second pack form) — opens CreditPackModal with an id-less
 * prefill, so it renders CreditPackForm in create mode with the computed credits size
 * pre-filled. Only `credits` is carried: since 2026-07-29 price lives per-(currency,
 * gateway) in CreditPackMarketsPanel, not on the pack form, so the simulated retail price
 * has nowhere to land here — the price knob stays purely a simulation input, and the
 * resulting pack still needs a market added afterward via the Preços/Mercados panel.
 */
function handleApplyToPack(): void {
  eventBus.emit<{ pack: Partial<AiCredits_PackRow> }>('modal.creditPack.open', {
    pack: {
      credits: Math.round(packSizeCredits.value * MICRO_PER_CREDIT),
    },
  });
}
</script>

<style scoped>
.credit-pricing-calculator {
  margin-top: 24px;
  padding: 20px;
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 12px;
}

.calculator-title {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--p-text-color);
}

.basis-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 500;
}

.basis-banner--real {
  background: #dcfce7;
  color: #166534;
}

.basis-banner--fallback {
  background: #ffedd5;
  color: #9a3412;
}

.tiles-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.tiles-row > * {
  flex: 1 1 calc(33% - 12px);
  min-width: 200px;
}

.knobs-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.knobs-row > * {
  flex: 1 1 calc(50% - 10px);
  min-width: 220px;
}

.apply-row {
  margin-top: 8px;
}
</style>
